import { Link } from '@tanstack/react-router';
import { Menu, Monitor, Moon, Search, Sun, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { cn } from '../../lib/cn';
import { useMediaQuery } from '../../lib/media';
import { useMe } from '../../lib/session';
import { applyTheme, readTheme, type ThemeChoice } from '../../lib/theme';
import { ActivityCounter } from '../activity/activity-counter';
import { NotificationsBell } from '../activity/notifications-bell';
import { UserMenu } from '../auth/user-menu';
import { ColorBySwitch } from '../colors/color-by';
import { HelpMenu } from '../guidance/help-menu';
import { useCommandPalette } from '../help/command-palette';
import { Button } from '../ui/button';
import { Tooltip } from '../ui/tooltip';
import { MonitoringBanner } from './monitoring-banner';
import { NAV } from './nav';
import { OfflineBanner } from './route-states';

const THEME_NEXT: Record<ThemeChoice, ThemeChoice> = { system: 'light', light: 'dark', dark: 'system' };
const THEME_ICON = { system: Monitor, light: Sun, dark: Moon };

function ThemeToggle() {
  const [theme, setTheme] = useState<ThemeChoice>('system');
  useEffect(() => setTheme(readTheme()), []);
  const Icon = THEME_ICON[theme];
  return (
    <Tooltip content={`Theme: ${theme} (click to change)`}>
      <Button
        variant="ghost"
        size="icon"
        aria-label={`Theme: ${theme}`}
        onClick={() => {
          const next = THEME_NEXT[theme];
          applyTheme(next);
          setTheme(next);
        }}
      >
        <Icon />
      </Button>
    </Tooltip>
  );
}

/**
 * The frame around every screen. From a tablet up: header, a side menu and the screen. On a phone the side menu folds
 * into a Menu button (it opens over the screen and closes when a screen is picked), the search box shrinks to its
 * icon and "Color by" moves into the menu, so no screen is wider than the phone.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const palette = useCommandPalette();
  const permissions = useMe().data?.permissions ?? [];
  const [menu, setMenu] = useState(false);
  // "Color by" sits in the header on a wide window and at the top of the menu otherwise (one copy at a time).
  const wide = useMediaQuery('(min-width: 1024px)');
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenu(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu]);
  return (
    <div className="grid min-h-dvh grid-cols-1 grid-rows-[3.5rem_1fr] md:grid-cols-[14rem_1fr]">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-surface focus:p-2">
        Skip to content
      </a>
      <header className="flex min-w-0 items-center gap-2 border-b bg-surface px-2 sm:gap-3 sm:px-4 md:col-span-2">
        <Button
          variant="ghost"
          size="icon"
          feedback={false}
          className="md:hidden"
          aria-label={menu ? 'Close menu' : 'Menu'}
          aria-expanded={menu}
          aria-controls="main-nav"
          onClick={() => setMenu(m => !m)}
        >
          {menu ? <X /> : <Menu />}
        </Button>
        <Link to="/" className="flex shrink-0 items-center gap-2 font-semibold tracking-tight">
          <span aria-hidden="true" className="grid size-7 place-items-center rounded-md bg-primary text-primary-foreground">
            H
          </span>
          <span className="sr-only sm:not-sr-only">Hoopoe</span>
        </Link>
        <button
          type="button"
          onClick={palette.open}
          className="flex h-9 shrink-0 items-center gap-2 rounded-md border border-input bg-background px-2.5 text-sm text-muted-foreground sm:ms-6 sm:w-full sm:max-w-md sm:shrink sm:px-3"
          // The accessible name carries the visible words (WCAG 2.5.3); on narrow screens only the icon shows.
          aria-label="Search actors, sources, help… ⌘K"
          aria-keyshortcuts="Meta+K Control+K"
          data-tour="search"
        >
          <Search className="size-4" aria-hidden="true" />
          <span className="hidden truncate sm:inline">Search actors, sources, help…</span>
          <kbd className="ms-auto hidden rounded border bg-muted px-1.5 font-mono text-xs sm:inline">⌘K</kbd>
        </button>
        <div className="ms-auto flex items-center gap-0.5 sm:gap-1">
          {wide && <ColorBySwitch className="me-2" />}
          <ActivityCounter />
          <NotificationsBell />
          <ThemeToggle />
          <HelpMenu />
          <UserMenu />
        </div>
      </header>
      <nav
        id="main-nav"
        aria-label="Main"
        className={cn(
          'border-e bg-surface p-2 md:static md:block',
          menu ? 'fixed inset-x-0 top-14 bottom-0 z-40 overflow-auto border-e-0 shadow-lg' : 'hidden',
        )}
      >
        {!wide && <ColorBySwitch className="mb-2 flex-wrap px-3 py-2" />}
        <ul className="flex flex-col gap-0.5">
          {NAV.filter(item => !item.permission || permissions.includes(item.permission)).map(item => (
            <li key={item.to}>
              <Link
                to={item.to}
                activeOptions={{ exact: item.to === '/' }}
                onClick={() => setMenu(false)}
                className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground data-[status=active]:bg-muted data-[status=active]:font-medium data-[status=active]:text-foreground"
              >
                <item.icon className="size-4" aria-hidden="true" />
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <main id="main" tabIndex={-1} className="relative min-w-0 overflow-auto p-4 focus-visible:outline-none md:p-6">
        <OfflineBanner />
        <MonitoringBanner />
        {children}
      </main>
    </div>
  );
}

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight break-words">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions}
    </div>
  );
}
/** Placeholder for screens delivered by later tasks. */
export function ComingSoon({ title, task }: { title: string; task: string }) {
  return (
    <>
      <PageHeader title={title} />
      <div className="rounded-lg border border-dashed bg-surface p-10 text-center text-sm text-muted-foreground">
        This screen arrives with task {task}.
      </div>
    </>
  );
}
